import { useEffect, useState } from "react";

type UseFadeTransitionOptions = {
  visible: boolean;
  duration?: number;
  delay?: number;
  enterDuration?: number;
  exitDuration?: number;
  enterDelay?: number;
  exitDelay?: number;
  onExited?: () => void;
};

/**
 * Hook to handle fade transitions with proper DOM mounting/unmounting
 * 
 * @param options.visible Whether the element should be visible
 * @param options.duration Transition duration in ms (default: 300)
 * @param options.delay Optional delay before starting exit animation (default: 0)
 * @param options.onExited Optional callback when element is completely removed
 * @returns Object with state and props for the fade transition
 */
export function useFadeTransition({
  visible,
  duration = 300,
  delay = 0,
  enterDuration,
  exitDuration,
  enterDelay,
  exitDelay,
  onExited
}: UseFadeTransitionOptions) {
  const resolvedEnterDuration = enterDuration ?? duration;
  const resolvedExitDuration = exitDuration ?? duration;
  const resolvedEnterDelay = enterDelay ?? delay;
  const resolvedExitDelay = exitDelay ?? delay;
  // Whether component is in DOM
  const [isMounted, setIsMounted] = useState(visible);
  // Whether component is visible (for CSS)
  const [isVisible, setIsVisible] = useState(visible);

  useEffect(() => {
    let timer: number;
    
    // Handle appearance
    if (visible) {
      setIsMounted(true);
      // Small delay to ensure DOM is ready before starting animation
      timer = window.setTimeout(() => {
        setIsVisible(true);
      }, 10 + resolvedEnterDelay);
    } 
    // Handle disappearance
    else {
      setIsVisible(false);
      // Wait for animation to complete before unmounting
      timer = window.setTimeout(() => {
        setIsMounted(false);
        onExited?.();
      }, resolvedExitDuration + resolvedExitDelay);
    }
    
    return () => clearTimeout(timer);
  }, [
    visible,
    onExited,
    resolvedEnterDelay,
    resolvedExitDelay,
    resolvedExitDuration,
  ]);

  // Props to spread on the component container
  const fadeProps = {
    style: {
      opacity: isVisible ? 1 : 0,
      transition: `opacity ${
        isVisible ? resolvedEnterDuration : resolvedExitDuration
      }ms ease-in-out ${
        isVisible ? resolvedEnterDelay : resolvedExitDelay
      }ms`,
      pointerEvents: isVisible ? "auto" : "none"
    }
  };

  return {
    isMounted,
    isVisible,
    fadeProps
  };
}